import React from 'react';
import { useProject } from '../../context/ProjectContext';
import { usePreviewRuntime } from './usePreviewRuntime';
import PreviewComponent from './PreviewComponent';
import styles from './Preview.module.css';
import ValidationSummary from '../ValidationSummary';
import type { ProjectDocument } from '../../types/project';
import type { PreviewActionExecutor } from './usePreviewRuntime';

// ── Helpers ───────────────────────────────────────────────────────────────────

export type CanvasDimensions = { width: number; height: number };
export type RenderedComponentBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

/** Compute the minimum canvas dimensions needed to fit all components. */
export function canvasBounds(components: { position: { x: number; y: number }; size: { width: number; height: number } }[]): CanvasDimensions {
  if (components.length === 0) return { width: 800, height: 600 };
  let maxX = 0;
  let maxY = 0;
  for (const c of components) {
    maxX = Math.max(maxX, c.position.x + c.size.width);
    maxY = Math.max(maxY, c.position.y + c.size.height);
  }
  return { width: Math.max(maxX + 48, 800), height: Math.max(maxY + 48, 400) };
}

/** Grow design-time bounds to contain wrappers that expand at runtime. */
export function expandCanvasBounds(
  designBounds: CanvasDimensions,
  renderedComponents: RenderedComponentBounds[],
): CanvasDimensions {
  let maxX = designBounds.width - 48;
  let maxY = designBounds.height - 48;
  for (const component of renderedComponents) {
    maxX = Math.max(maxX, component.left + component.width);
    maxY = Math.max(maxY, component.top + component.height);
  }
  return {
    width: Math.max(designBounds.width, maxX + 48),
    height: Math.max(designBounds.height, maxY + 48),
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

function pageSlug(page: ProjectDocument['project']['pages'][number]): string {
  return page.slug || page.name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || page.id;
}

function Preview({ document, projectName: suppliedName, executor, published = false, initialPageSlug }: { document?: ProjectDocument; projectName?: string; executor?: PreviewActionExecutor; published?: boolean; initialPageSlug?: string }): React.ReactElement {
  const project = useProject();
  const doc = document ?? project.doc;
  const projectName = suppliedName ?? project.projectName;
  const pages = doc.project.pages;

  // Page selection (tabs when multiple pages; single page for MVP)
  const defaultIndex = Math.max(0, pages.findIndex(page => page.id === doc.project.settings?.defaultPageId));
  const requestedIndex = initialPageSlug ? pages.findIndex(page => pageSlug(page) === initialPageSlug) : -1;
  const [activePageIndex, setActivePageIndex] = React.useState(requestedIndex >= 0 ? requestedIndex : defaultIndex);
  const activePage = pages[activePageIndex] ?? null;
  React.useEffect(() => {
    if (!published) return;
    const onPopState = () => {
      const slug = window.location.pathname.split('/')[3];
      const index = slug ? pages.findIndex(page => pageSlug(page) === decodeURIComponent(slug)) : defaultIndex;
      if (index >= 0) setActivePageIndex(index);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [defaultIndex, pages, published]);

  const bounds = React.useMemo(
    () => activePage ? canvasBounds(activePage.components) : { width: 800, height: 400 },
    [activePage],
  );
  const canvasRef = React.useRef<HTMLDivElement | null>(null);
  const [runtimeBounds, setRuntimeBounds] = React.useState<CanvasDimensions>(bounds);

  React.useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !activePage) {
      setRuntimeBounds(bounds);
      return;
    }

    const wrappers = Array.from(canvas.children)
      .filter((child): child is HTMLElement => child instanceof HTMLElement);
    const measure = () => {
      const rendered = wrappers.map((wrapper) => ({
        left: wrapper.offsetLeft,
        top: wrapper.offsetTop,
        width: Math.max(wrapper.offsetWidth, wrapper.scrollWidth),
        height: Math.max(wrapper.offsetHeight, wrapper.scrollHeight),
      }));
      const next = expandCanvasBounds(bounds, rendered);
      setRuntimeBounds((current) => (
        current.width === next.width && current.height === next.height ? current : next
      ));
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(measure);
    wrappers.forEach((wrapper) => observer.observe(wrapper));
    return () => observer.disconnect();
  }, [activePage, bounds]);
  const displayBounds = {
    width: Math.max(bounds.width, runtimeBounds.width),
    height: Math.max(bounds.height, runtimeBounds.height),
  };

  // ── Preview runtime (binding execution, component state) ─────────────────
  const selectPage = React.useCallback((index: number) => {
    setActivePageIndex(index);
    if (published) {
      const next = pages[index];
      const appSlug = window.location.pathname.split('/')[2];
      if (next && appSlug) window.history.pushState({}, '', `/apps/${appSlug}/${pageSlug(next)}`);
    }
  }, [pages, published]);
  const runtime = usePreviewRuntime(doc, executor, activePage?.id, pageId => {
    const index = pages.findIndex(page => page.id === pageId);
    if (index >= 0) selectPage(index);
  });

  return (
    <div className={styles.preview}>

      {/* ── Preview header ────────────────────────────────────────── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.badge}>{published ? 'Published app' : 'Preview'}</span>
          <span className={styles.projectName}>{projectName}</span>
        </div>
        {!published && <span className={styles.hint}>Read-only render of the current project. Switch to Visual Editor or JSON Editor to make changes.</span>}
      </div>
      {!published && <ValidationSummary />}

      {/* ── Page tabs (shown only when more than one page exists) ─── */}
      {runtime.pageLoadState.status !== 'idle' && (
        <div
          className={[
            styles.pageLoadStatus,
            runtime.pageLoadState.status === 'error' ? styles.pageLoadError : '',
          ].join(' ')}
          role={runtime.pageLoadState.status === 'error' ? 'alert' : 'status'}
          data-testid="page-load-status"
        >
          {runtime.pageLoadState.message}
        </div>
      )}

      {pages.length > 1 && (
        <div className={styles.tabs}>
          {pages.map((page, i) => page.showInNavigation !== false && (
            <button
              key={page.id}
              className={[styles.tab, i === activePageIndex ? styles.tabActive : ''].join(' ')}
              onClick={() => selectPage(i)}
            >
              {page.name}
            </button>
          ))}
        </div>
      )}

      {/* ── Canvas area ───────────────────────────────────────────── */}
      <div className={styles.canvasScroll}>
        {!activePage || activePage.components.length === 0 ? (
          <div className={styles.empty}>
            <p className={styles.emptyTitle}>Nothing to preview</p>
            <p className={styles.emptyHint}>
              Add components in the Visual Editor or JSON Editor, then return here.
            </p>
          </div>
        ) : (
          <div
            className={styles.canvas}
            ref={canvasRef}
            style={{ width: displayBounds.width, height: displayBounds.height }}
          >
            {activePage.components.map((c) => (
              <PreviewComponent
                key={c.id}
                component={c}
                runtimeState={runtime.componentState[c.id]}
                isLoading={runtime.buttonLoading[c.id] === true}
                onButtonClick={runtime.handleButtonClick}
                onTextInputChange={runtime.handleTextInputChange}
                onDropdownChange={runtime.handleDropdownChange}
                onTableRowSelect={runtime.handleTableRowSelect}
                onValueChange={runtime.handleValueChange}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Preview;
